/**
 * The Create form's prompt plus the shared genre / vocal / language selection.
 *
 * Create's prompt cannot be component-local: three other features hand words over to
 * Create and expect them to be there on arrival — Music DNA ("Generate from this DNA"),
 * and the Vault's "load prompt" and "load preset" actions. In the monolith those were
 * direct setPrompt calls from four screens away, which is a large part of why the state
 * was unmovable.
 *
 * Image Music now keeps its OWN prompt text in its own draft state. The user asked that
 * words typed on /create must not appear in /image-music, so only the style choices stay
 * shared between the two routes.
 *
 * Genre and vocal start unselected so the prompt can carry those instructions; language
 * starts on English. Each chip can still be selected and cleared by the user.
 */
import { create } from 'zustand';
import { DEFAULT_LANGUAGE } from '../data/music';
import { STANDARD_MUSIC_MODEL, type MusicGenerationModel } from '../lib/musicGenerationModels';

export type CreateSurface = 'create' | 'imageMusic';

/** The two prompt surfaces that can each hold their own generated result card. */
const EMPTY_RESULT_SONG_IDS: Record<CreateSurface, string | null> = {
  create: null,
  imageMusic: null,
};

/**
 * The staged Image Music reference image.
 *
 * Kept in a store rather than in the route so leaving and returning finds the same
 * picture, filename and request body the user left behind.
 */
export interface ImageMusicReferenceImage {
  dataUrl: string;
  mimeType: string;
  filename: string;
}

interface ImageMusicDraftState {
  prompt: string;
  image: ImageMusicReferenceImage | null;
  promptOpen: boolean;
}

const EMPTY_IMAGE_MUSIC_DRAFT: ImageMusicDraftState = {
  prompt: '',
  image: null,
  promptOpen: false,
};
interface CreateState {
  prompt: string;
  model: MusicGenerationModel;
  genre: string;
  vocal: string;
  /**
   * Language the vocals should be in.
   *
   * Here rather than in the component for the same reason as the other three: the form
   * survives navigating away, and the handover from Music DNA and the Vault sets fields
   * on it from another route. It reaches the request as
   * CreateGenerationInput.language — see api/generationApi.ts.
   */
  language: string;

  /**
   * Which generated song each prompt surface should show, if any.
   *
   * Create and Image Music share the same composer fields on purpose, but they do NOT
   * share one result card: the user asked that a song appear only under the prompt box
   * that created it. Holding one slot per surface is what makes that possible while still
   * keeping explicit ownership of the shown result.
   */
  resultSongIds: Record<CreateSurface, string | null>;

  /**
   * The visible Image Music draft state that must survive route unmounts.
   *
   * `over` and `reading` stay route-local because they are momentary UI states; the
   * chosen image, the optional prompt text and whether the prompt box is open are what
   * define how the screen looks when the user comes back to it.
   */
  imageMusic: ImageMusicDraftState;

  setPrompt: (prompt: string) => void;
  setModel: (model: MusicGenerationModel) => void;
  setGenre: (genre: string) => void;
  setVocal: (vocal: string) => void;
  setLanguage: (language: string) => void;
  setSurfaceResultSongId: (surface: CreateSurface, songId: string | null) => void;
  setImageMusicPrompt: (prompt: string) => void;
  setImageMusicImage: (image: ImageMusicReferenceImage | null) => void;
  setImageMusicPromptOpen: (promptOpen: boolean) => void;
  /** Loads a prompt from another feature, optionally with a genre, vocal and language. */
  load: (input: { prompt: string; genre?: string; vocal?: string; language?: string }) => void;
}

export const useCreateStore = create<CreateState>((set) => ({
  prompt: '',
  model: STANDARD_MUSIC_MODEL,
  genre: '',
  vocal: '',
  language: DEFAULT_LANGUAGE,
  resultSongIds: { ...EMPTY_RESULT_SONG_IDS },
  imageMusic: { ...EMPTY_IMAGE_MUSIC_DRAFT },

  setPrompt(prompt) {
    set({ prompt });
  },
  setModel(model) {
    set({ model });
  },
  setGenre(genre) {
    set({ genre });
  },
  setVocal(vocal) {
    set({ vocal });
  },
  setLanguage(language) {
    set({ language });
  },
  setSurfaceResultSongId(surface, songId) {
    set((state) => ({ resultSongIds: { ...state.resultSongIds, [surface]: songId } }));
  },
  setImageMusicPrompt(prompt) {
    set((state) => ({ imageMusic: { ...state.imageMusic, prompt } }));
  },
  setImageMusicImage(image) {
    set((state) => ({
      imageMusic: {
        ...state.imageMusic,
        image,
      },
    }));
  },
  setImageMusicPromptOpen(promptOpen) {
    set((state) => ({ imageMusic: { ...state.imageMusic, promptOpen } }));
  },
  load({ prompt, genre, vocal, language }) {
    set({
      prompt,
      ...(genre !== undefined ? { genre } : null),
      ...(vocal ? { vocal } : null),
      // Absent leaves the current choice standing rather than resetting to English:
      // the Genre Mixer and Music DNA hand over a prompt and a genre and say nothing
      // about language, and silently discarding the user's own selection on the way in
      // is not something either of them asked for.
      ...(language ? { language } : null),
    });
  },
}));
